import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import headerImage from '../../../assets/header_v4.png';
import { branchesApi, vendorsApi, productsApi, referralPlansApi } from '@/lib/apiServices';
import { useApiList } from '@/lib/hooks/useApiList';
import { fmt } from '@/lib/format';
import { getWholesaleInfo, resolveUnitPrice } from '@/lib/pricing';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getMyReferralLink } from '@/features/customers/wallet/api';
import '../content.css';

const FEATURES = [
  { icon: 'fa-truck', title: 'ارسال سریع', description: 'تحویل کالاهای اساسی در کمترین زمان ممکن' },
  { icon: 'fa-wallet', title: 'پشتیبانی کالابرگ', description: 'خرید کالاهای اساسی با استفاده از کالابرگ' },
  { icon: 'fa-check-circle', title: 'کیفیت و قیمت تضمینی', description: 'تمامی محصولات دارای ضمانت اصالت کالا هستند' },
];

function LoadingCards({ count = 4, columns = 4 }) {
  return (
    <div
      className={`home-skeleton-grid${columns === 3 ? ' home-skeleton-grid-three' : ''}`}
      role="status"
      aria-label="در حال بارگذاری"
    >
      {Array.from({ length: count }, (_, index) => (
        <div className="home-skeleton-card" key={index} aria-hidden="true">
          <span className="home-skeleton-image" />
          <span className="home-skeleton-line" />
          <span className="home-skeleton-line home-skeleton-line-short" />
        </div>
      ))}
    </div>
  );
}

function SectionMessage({ children, error = false }) {
  return (
    <p className={`home-section-state${error ? ' is-error' : ''}`} role={error ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

function HomeProductImage({ product }) {
  const [failed, setFailed] = useState(false);
  if (!product.image_url || failed) {
    return <span className="home-product-image-placeholder">تصویر در دسترس نیست</span>;
  }
  return (
    <img
      src={product.image_url}
      alt={product.name_fa || product.name_en}
      width="400"
      height="400"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function Home() {
  const branches = useApiList(() => branchesApi.list(), []);
  const vendors = useApiList(() => vendorsApi.list(), []);
  const products = useApiList(() => productsApi.list({ page: 1, size: 8 }), []);
  const referralPlans = useApiList(() => referralPlansApi.list({ page: 1, size: 3 }), []);
  const { user, activeRole } = useAuth();
  const isCustomer = Boolean(user) && activeRole === 'customer';
  const [referralLink, setReferralLink] = useState(null);
  const [referralLinkError, setReferralLinkError] = useState('');
  const [referralLinkLoading, setReferralLinkLoading] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!isCustomer) {
      return () => {
        cancelled = true;
      };
    }

    Promise.resolve()
      .then(() => {
        if (cancelled) return null;
        setReferralLink(null);
        setReferralLinkError('');
        setReferralLinkLoading(true);
        return getMyReferralLink();
      })
      .then((result) => {
        if (!cancelled && result) setReferralLink(result);
      })
      .catch(() => {
        if (!cancelled) setReferralLinkError('دریافت کد معرفی شما ممکن نشد. دوباره تلاش کنید.');
      })
      .finally(() => {
        if (!cancelled) setReferralLinkLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCustomer]);

  const branchesById = new Map(branches.data.map((branch) => [branch.id, branch]));
  const vendorsById = new Map(vendors.data.map((vendor) => [vendor.id, vendor]));
  const directVendors = vendors.data.filter((vendor) => vendor.is_direct_to_consumer).slice(0, 6);
  const referralPlansData = referralPlans.data.slice(0, 3);

  const getProductSeller = (product) => {
    const seller = product.vendor_id
      ? vendorsById.get(product.vendor_id)
      : branchesById.get(product.admin_id);
    if (seller?.vendor_shop_name_fa) return seller.vendor_shop_name_fa;
    if (product.vendor_id) return 'فروشندهٔ ایران چارسوق';
    return seller?.location?.name_fa
      ? `شعبهٔ ${seller.location.name_fa}`
      : 'شعبهٔ ایران چارسوق';
  };

  const copyReferralCode = async () => {
    if (!referralLink?.referral_code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralLink.referral_code);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch {
      try {
        const input = document.createElement('textarea');
        input.value = referralLink.referral_code;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        try {
          input.select();
          const copied = document.execCommand('copy');
          if (!copied) throw new Error('Copy command failed');
        } finally {
          input.remove();
        }
      } catch {
        setCopyFeedback('کپی کد انجام نشد؛ کد را انتخاب و دستی کپی کنید.');
        return;
      }
    }
    setCopyFeedback('کد معرفی کپی شد.');
  };

  return (
    <div className="home-page">
      <header className="hero">
        <img
          className="hero-image"
          src={headerImage}
          alt=""
          aria-hidden="true"
          width="1916"
          height="821"
          loading="eager"
          fetchPriority="high"
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <span className="hero-eyebrow">فروشگاه آنلاین ایران چارسوق</span>
          <h1>همه نیازهای شما، در یک فروشگاه</h1>
          <p>
            از محصولات غذایی و روزمره تا کالاهای صنعتی و ساختمانی — با
            پشتیبانی کالابرگ و ارسال سریع به سراسر ایران
          </p>
          <div className="hero-actions">
            <Link to="/products" className="btn btn-primary">مشاهده محصولات</Link>
            <Link to="/marketplace" className="btn btn-ghost">فروشندگان ما</Link>
          </div>
        </div>
      </header>

      <section className="features">
        {FEATURES.map((feature) => (
          <div key={feature.title} className="feature-card">
            <div className="feature-icon">
              <i className={`fas ${feature.icon}`} aria-hidden="true" />
            </div>
            <h3>{feature.title}</h3>
            <p>{feature.description}</p>
          </div>
        ))}
      </section>

      {/* Featured Products */}
      <section className="home-section container">
        <div className="home-section-header">
          <h2>محصولات پرطرفدار</h2>
          <Link to="/products" className="home-section-link">مشاهده همه ←</Link>
        </div>

        {products.loading ? (
          <LoadingCards count={4} />
        ) : products.error ? (
          <SectionMessage error>دریافت محصولات ممکن نشد. لطفاً دوباره تلاش کنید.</SectionMessage>
        ) : products.data.length === 0 ? (
          <SectionMessage>محصولی برای نمایش یافت نشد.</SectionMessage>
        ) : (
          <div className="home-products-grid">
            {products.data.slice(0, 8).map((product) => {
              const wholesale = getWholesaleInfo(product, 1);

              return (
                <Link key={product.id} to={`/product/${product.id}`} className="home-product-card">
                  <div className="home-product-image">
                    <HomeProductImage product={product} />
                  </div>
                  <h4>{product.name_fa || product.name_en}</h4>
                  <span className="home-product-seller">{getProductSeller(product)}</span>
                  <p className="home-product-price">{fmt(resolveUnitPrice(product, 1))} تومان</p>
                  {wholesale && !wholesale.eligible && (
                    <span className="home-product-wholesale-hint">
                      عمده {fmt(wholesale.unitPrice)} تومان
                      <span>از {fmt(wholesale.minQuantity)} عدد</span>
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <section className="home-section referral-plans-section">
        <div className="home-section-header">
          <h2>با معرفی دوستان درآمد کسب کنید</h2>
        </div>
        <p className="referral-plans-intro">
          با کد معرفی خود، دوستانتان را به خرید دعوت کنید و بر اساس خریدهای واجد شرایط پورسانت بگیرید.
        </p>
        <ol className="referral-steps">
          <li><span>۱</span> کد معرفی‌تان را دریافت کنید.</li>
          <li><span>۲</span> کد را با دوستانتان به اشتراک بگذارید.</li>
          <li><span>۳</span> با خرید آن‌ها، پورسانت مطابق طرح ثبت می‌شود.</li>
        </ol>

        {referralPlans.loading ? (
          <LoadingCards count={3} columns={3} />
        ) : referralPlans.error ? (
          <SectionMessage error>دریافت طرح‌های معرفی ممکن نشد. لطفاً دوباره تلاش کنید.</SectionMessage>
        ) : referralPlansData.length === 0 ? (
          <SectionMessage>در حال حاضر طرح معرفی فعالی برای نمایش وجود ندارد.</SectionMessage>
        ) : (
          <div className="referral-plans-grid">
            {referralPlansData.map((plan) => (
              <article className="referral-plan-card" key={plan.id}>
                <h3>{plan.name}</h3>
                <p className="referral-plan-discount">
                  تخفیف طرح: <strong>{fmt(plan.discount_percent)}٪</strong>
                </p>
              </article>
            ))}
          </div>
        )}

        <div className="referral-plans-action">
          {!user ? (
            <Link to="/signup?intent=referral" className="btn btn-primary">
              ثبت‌نام و دریافت کد معرفی
            </Link>
          ) : isCustomer ? (
            <>
              {referralLinkLoading ? (
                <span className="referral-link-loading" role="status">در حال دریافت کد معرفی…</span>
              ) : referralLinkError ? (
                <span className="referral-link-error" role="alert">{referralLinkError}</span>
              ) : referralLink?.referral_code ? (
                <div className="referral-code-box">
                  <span>کد معرفی شما:</span>
                  <bdi dir="ltr">{referralLink.referral_code}</bdi>
                  <button type="button" className="referral-copy-button" onClick={copyReferralCode}>
                    کپی کد
                  </button>
                </div>
              ) : null}
              <Link to="/profile/referral-tree" className="referral-tree-link">
                مشاهده پنل معرفی
              </Link>
              <span className="referral-copy-feedback" aria-live="polite">{copyFeedback}</span>
            </>
          ) : (
            <p className="referral-neutral-note">
              طرح‌های معرفی برای حساب‌های مشتری در نظر گرفته شده‌اند.
            </p>
          )}
        </div>
      </section>

      <section className="home-section container">
        <div className="home-section-header">
          <h2>شعب ایران چارسوق</h2>
          <Link to="/marketplace" className="home-section-link">مشاهده همه ←</Link>
        </div>

        {branches.loading ? (
          <LoadingCards count={3} columns={3} />
        ) : branches.error ? (
          <SectionMessage error>دریافت شعب ممکن نشد. لطفاً دوباره تلاش کنید.</SectionMessage>
        ) : branches.data.length === 0 ? (
          <SectionMessage>شعبه‌ای برای نمایش ثبت نشده است.</SectionMessage>
        ) : (
          <div className="home-scroll-wrapper">
            <div className="home-scroll-row" role="region" aria-label="شعب ایران چارسوق" tabIndex={0}>
              {branches.data.slice(0, 6).map((branch) => (
                <Link key={branch.id} to={`/branches/${branch.id}`} className="home-scroll-card">
                  <div className="home-scroll-card-banner">
                    {branch.vendor_banner_url ? (
                      <img src={branch.vendor_banner_url} alt={branch.vendor_shop_name_fa || ''} loading="lazy" />
                    ) : (
                      <span>{branch.vendor_shop_name_fa?.[0] || branch.location?.name_fa?.[0]}</span>
                    )}
                  </div>
                  <h4>
                    {branch.vendor_shop_name_fa
                      || (branch.location?.name_fa ? `شعبهٔ ${branch.location.name_fa}` : 'شعبه ایران چارسوق')}
                  </h4>
                  {branch.vendor_shop_name_fa && branch.location && (
                    <p className="home-scroll-card-location">
                      <i className="fas fa-map-marker-alt" aria-hidden="true" /> {branch.location.name_fa}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Featured Vendors */}
      <section className="home-section container">
        <div className="home-section-header">
          <h2>عرضه مستقیم از درب کارخانه</h2>
          <Link to="/marketplace" className="home-section-link">مشاهده همه ←</Link>
        </div>

        {vendors.loading ? (
          <LoadingCards count={3} columns={3} />
        ) : vendors.error ? (
          <SectionMessage error>دریافت فروشندگان ممکن نشد. لطفاً دوباره تلاش کنید.</SectionMessage>
        ) : directVendors.length === 0 ? (
          <SectionMessage>فروشندهٔ عرضهٔ مستقیم برای نمایش ثبت نشده است.</SectionMessage>
        ) : (
          <div className="home-vendors-grid">
            {directVendors.map((vendor) => (
              <Link key={vendor.id} to={`/vendors/${vendor.id}`} className="home-scroll-card">
                <div className="home-scroll-card-banner">
                  {vendor.vendor_banner_url ? (
                    <img src={vendor.vendor_banner_url} alt={vendor.vendor_shop_name_fa} loading="lazy" />
                  ) : (
                    <span>{vendor.vendor_shop_name_fa?.[0]}</span>
                  )}
                </div>
                <h4>{vendor.vendor_shop_name_fa}</h4>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}